import { useState, useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { Text } from '@mercator-gcp/grout';
import SearchInput from 'components/elements/SearchInput';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { Pagination } from 'components/layout/List/Paging';
import DashboardLeadsTable from 'components/widgets/Dashboard/DashboardLeadsTable';
import GroupSettingsMenu from 'components/widgets/Dashboard/GroupSettingsMenu';
import CategoryFilters from 'components/widgets/ProjectFilterToolbar/CategoryFilters';
import { useDebounce } from 'hooks/useDebounced';
import { useFilterGroupConfigLeads } from 'hooks/useFilterGroupConfigLeads';
import { useGroupData } from 'hooks/useGroupData';
import { useHiddenProjects } from 'hooks/useHiddenProjects';
import { useSelectedLeadFilters } from 'hooks/useLeadTableFiltering';
import { usePaginate } from 'hooks/usePaginate';
import { useSortedProjects } from 'hooks/useSortedProjects';
import { useDashboardGroupName } from 'state/dashboard';
import { useSelectedGroupQuery } from 'state/queryParams';
import { PaginatedProject } from 'types';
import { FilterViewEnum } from 'types/FilterProjects';
import { ProjectGroupTypes } from 'utils/analytics/constants';
import { formatInteger } from 'utils/format';

const filtersStyle = {
  gap: 'padding_16',
  justifyContent: 'space-between',
};

const searchInputStyle = {
  maxWidth: '240px',
  flex: 1,
};

const GroupDetails = () => {
  const [group] = useSelectedGroupQuery();
  const [groupName] = useDashboardGroupName();
  const [pageSize] = useState<number>(20);
  const [keywordFilter, setKeywordFilter] = useState('');
  const { nextPage, previousPage, page: currentPage, setPageNumber } = usePaginate();
  const { selectedLeadFilters: activeTabs } = useSelectedLeadFilters();
  const { sortArray } = useSortedProjects();
  const debouncedKeywordFilter = useDebounce(keywordFilter, 500);
  const { getFilteredLeads } = useFilterGroupConfigLeads({
    groupConfigId: Number(group),
    currentPage,
    sortArray,
    activeTabs,
    keywords: debouncedKeywordFilter,
  });
  const { data: filteredGroupConfigLeads, isLoading } = getFilteredLeads;

  const { getAllHiddenProjectIdsForUser } = useHiddenProjects();
  const { data: hiddenProjectsSet } = getAllHiddenProjectIdsForUser;
  const groupData = useGroupData(String(group));

  const changeLURDtoEntitlment = (item: PaginatedProject) =>
    item?.city === 'Los Angeles' &&
    item?.latest_activity?.type.toLowerCase() === 'land use redesignation'
      ? {
          ...item,
          category: item.category.replace('land use redesignation', 'Entitlement'),
          latest_activity: {
            ...item.latest_activity,
            type: item?.latest_activity?.type.replace(
              'Land Use Redesignation',
              'Entitlement',
            ),
          },
        }
      : item;

  const { filteredProjectData, hiddenProjectsCount } = useMemo(() => {
    let countHiddenProjects = 0;
    if (!filteredGroupConfigLeads?.results) {
      return {
        filteredProjectData: [],
        hiddenProjectsCount: 0,
      };
    }

    let filteredList = filteredGroupConfigLeads.results?.map((item) =>
      changeLURDtoEntitlment(item),
    );

    if (!activeTabs.includes(FilterViewEnum.HIDDEN)) {
      filteredList = filteredList.filter((item) => {
        if (hiddenProjectsSet?.has(item.slug)) {
          countHiddenProjects += 1;

          return false;
        }

        return true;
      });
    }

    return {
      filteredProjectData: filteredList,
      hiddenProjectsCount: countHiddenProjects,
    };
  }, [filteredGroupConfigLeads?.results, hiddenProjectsSet]);

  const totalProjectCount = useMemo(() => {
    if (!filteredGroupConfigLeads?.count) {
      return 0;
    }

    return filteredGroupConfigLeads.count - hiddenProjectsCount;
  }, [filteredGroupConfigLeads?.count, hiddenProjectsCount]);

  const totalPages = useMemo(() => {
    return Math.ceil(totalProjectCount / pageSize) || 1;
  }, [totalProjectCount, pageSize]);

  return (
    <FlexColumn
      sx={{
        height: '100%',
        width: '100%',
        gap: 'padding_16',
      }}
    >
      <FlexRow>
        <FlexRow.Left>
          <Text.H3>{groupName}</Text.H3>
        </FlexRow.Left>
        <FlexRow.Right>
          <GroupSettingsMenu id={group ?? 'not_found'} group={groupData} />
        </FlexRow.Right>
      </FlexRow>

      <FlexRow sx={filtersStyle}>
        <CategoryFilters
          collectionOrGroup="group"
          visibleTabs={[
            FilterViewEnum.ALL,
            FilterViewEnum.NO_STATUS,
            FilterViewEnum.STAR,
            FilterViewEnum.WATCH,
            FilterViewEnum.HIDDEN,
          ]}
        />
        <Box sx={searchInputStyle}>
          <SearchInput
            value={keywordFilter}
            onChange={setKeywordFilter}
            placeholder="Search projects by keyword"
          />
        </Box>
      </FlexRow>
      <DashboardLeadsTable
        items={filteredProjectData}
        leadContext={{
          groupId: group,
          groupType: ProjectGroupTypes.PROJECT_GROUP,
        }}
        isLoading={isLoading}
        header={
          !isLoading
            ? `${formatInteger(totalProjectCount)} active projects found`
            : undefined
        }
        footer={
          <Pagination
            currentPage={currentPage}
            isLoading={isLoading}
            setPage={(pageNumber) => setPageNumber(pageNumber)}
            totalPages={totalPages}
            onNext={() => nextPage()}
            onPrev={() => previousPage()}
          />
        }
      />
    </FlexColumn>
  );
};

export default GroupDetails;
